import React, { useMemo, useState } from 'react'
import { DateTime } from 'luxon'

import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    Divider,
    IconButton,
    Tooltip,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'
import {
    Close,
    DirectionsWalk,
    Email as EmailIcon,
    Phone as PhoneIcon,
    PersonOutline,
    Schedule,
    CheckCircle as CompleteIcon,
    HighlightOff as NoShowIcon,
    Assignment as GuestCardIcon,
} from '@material-ui/icons'

import { ApartmentTour, LeadTourStatus } from '../../models/Outreach/types'
import { useLeadTour } from '../../hooks/Outreach/useLeadTour'
import { SubmitLeadGuestCardDialog } from './SubmitLeadGuestCardDialog'
import { ViewGuestCardResultsDialog } from './ViewGuestCardResultsDialog'

const STATUS_STYLE: Record<
    LeadTourStatus,
    { bg: string; fg: string; label: string }
> = {
    scheduled: { bg: '#dbeafe', fg: '#1d4ed8', label: 'Scheduled' },
    completed: { bg: '#d1fae5', fg: '#047857', label: 'Completed' },
    no_show: { bg: '#ffe4e6', fg: '#be123c', label: 'No Show' },
    skipped: { bg: '#f3f4f6', fg: '#6b7280', label: 'Skipped' },
}

const useStyles = makeStyles((theme: Theme) => ({
    paper: {
        borderRadius: 16,
        maxWidth: 560,
        width: '100%',
        overflow: 'hidden',
    },
    header: {
        background: '#f9fafb',
        borderBottom: `1px solid ${theme.palette.divider}`,
        color: '#1f2937',
        padding: theme.spacing(2.5, 3),
        position: 'relative' as const,
    },
    closeBtn: {
        position: 'absolute' as const,
        top: theme.spacing(1),
        right: theme.spacing(1),
        color: '#9ca3af',
        '&:hover': {
            color: '#374151',
            backgroundColor: '#f3f4f6',
        },
    },
    title: {
        fontSize: '1.15rem',
        fontWeight: 700,
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        color: '#1f2937',
    },
    titleIcon: {
        width: 36,
        height: 36,
        borderRadius: 10,
        backgroundColor: '#fce7f3',
        color: '#db2777',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    subtitle: {
        fontSize: '0.8rem',
        color: '#6b7280',
        marginTop: 4,
        marginLeft: 44,
    },
    content: {
        padding: theme.spacing(2, 2.5, 2.5),
        maxHeight: '60vh',
        overflowY: 'auto',
    },
    sectionLabel: {
        fontSize: '0.65rem',
        fontWeight: 700,
        textTransform: 'uppercase' as const,
        letterSpacing: '0.05em',
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(1),
        marginTop: theme.spacing(1.5),
    },
    tourCard: {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 12,
        padding: theme.spacing(1.75, 2),
        marginBottom: theme.spacing(1),
        '&:last-child': {
            marginBottom: 0,
        },
    },
    tourTop: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(0.75),
    },
    leadName: {
        fontWeight: 700,
        fontSize: '0.9rem',
        color: '#1f2937',
    },
    metaRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        fontSize: '0.78rem',
        color: '#6b7280',
        marginTop: 3,
    },
    contactRow: {
        display: 'flex',
        flexWrap: 'wrap' as const,
        gap: theme.spacing(1.5),
        marginTop: theme.spacing(1),
        paddingTop: theme.spacing(1),
        borderTop: `1px solid ${theme.palette.divider}`,
    },
    contactItem: {
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        fontSize: '0.72rem',
        color: '#6b7280',
    },
    notes: {
        marginTop: theme.spacing(0.75),
        fontSize: '0.75rem',
        color: '#4b5563',
        fontStyle: 'italic',
    },
    empty: {
        textAlign: 'center' as const,
        padding: theme.spacing(4, 2),
        color: theme.palette.text.secondary,
    },
    tourActions: {
        display: 'flex',
        gap: theme.spacing(1),
        marginTop: theme.spacing(1),
        paddingTop: theme.spacing(1),
        borderTop: `1px solid ${theme.palette.divider}`,
    },
    actionBtn: {
        textTransform: 'none' as const,
        fontWeight: 600,
        fontSize: '0.72rem',
        borderRadius: 6,
        padding: theme.spacing(0.5, 1.25),
    },
    footer: {
        display: 'flex',
        justifyContent: 'flex-end',
        padding: theme.spacing(1.5, 2.5, 2),
    },
}))

const formatTourTime = (
    startsAt: string | null,
    endsAt: string | null,
    timezone: string,
): string => {
    if (!startsAt) return 'No time set'
    const start = DateTime.fromISO(startsAt, { zone: 'utc' }).setZone(timezone)
    if (!start.isValid) return 'No time set'
    if (endsAt) {
        const end = DateTime.fromISO(endsAt, { zone: 'utc' }).setZone(timezone)
        if (end.isValid) {
            return `${start.toFormat('h:mm a')} – ${end.toFormat('h:mm a')}`
        }
    }
    return start.toFormat('h:mm a')
}

interface TourDayModalProps {
    open: boolean
    onClose: () => void
    dateKey: string | null
    tours: ApartmentTour[]
    timezone: string
}

export const TourDayModal: React.FC<TourDayModalProps> = ({
    open,
    onClose,
    dateKey,
    tours,
    timezone,
}) => {
    const classes = useStyles()
    const { updateLeadTour } = useLeadTour()
    const [updatingTourId, setUpdatingTourId] = useState<number | null>(null)
    const [localTours, setLocalTours] = useState<ApartmentTour[]>(tours)
    const [guestCardTour, setGuestCardTour] = useState<ApartmentTour | null>(
        null,
    )
    const [
        viewGuestCardTour,
        setViewGuestCardTour,
    ] = useState<ApartmentTour | null>(null)

    React.useEffect(() => {
        setLocalTours(tours)
    }, [tours])

    const dateLabel = useMemo(() => {
        if (!dateKey) return 'Tours'
        const dt = DateTime.fromISO(dateKey, { zone: timezone })
        if (!dt.isValid) return 'Tours'
        return dt.toFormat('EEEE, MMMM d')
    }, [dateKey, timezone])

    const { scheduled, unscheduled } = useMemo(() => {
        const withTime: ApartmentTour[] = []
        const withoutTime: ApartmentTour[] = []
        localTours.forEach((t) => {
            if (t.starts_at) withTime.push(t)
            else withoutTime.push(t)
        })
        withTime.sort((a, b) =>
            (a.starts_at ?? '').localeCompare(b.starts_at ?? ''),
        )
        return { scheduled: withTime, unscheduled: withoutTime }
    }, [localTours])

    const handleMarkStatus = async (
        tour: ApartmentTour,
        status: 'completed' | 'no_show',
    ) => {
        setUpdatingTourId(tour.id)
        try {
            await updateLeadTour({
                leadId: tour.lead.id,
                tourId: tour.id,
                payload: { status },
            })
            setLocalTours((prev) =>
                prev.map((t) => (t.id === tour.id ? { ...t, status } : t)),
            )
        } catch {
            // error handled in hook
        } finally {
            setUpdatingTourId(null)
        }
    }

    const renderTour = (tour: ApartmentTour) => {
        const status = STATUS_STYLE[tour.status] ?? STATUS_STYLE.scheduled
        const isScheduled = tour.status === 'scheduled'
        const isUpdating = updatingTourId === tour.id
        return (
            <Box key={tour.id} className={classes.tourCard}>
                <Box className={classes.tourTop}>
                    <Box>
                        <Typography className={classes.leadName}>
                            {tour.lead.name || 'Unknown prospect'}
                        </Typography>
                        <Box className={classes.metaRow}>
                            <Schedule style={{ fontSize: 14 }} />
                            {formatTourTime(
                                tour.starts_at,
                                tour.ends_at,
                                timezone,
                            )}
                        </Box>
                        <Box className={classes.metaRow}>
                            <PersonOutline style={{ fontSize: 14 }} />
                            {tour.given_by?.name || 'Unassigned'}
                        </Box>
                    </Box>
                    <Chip
                        size="small"
                        label={status.label}
                        style={{
                            backgroundColor: status.bg,
                            color: status.fg,
                            fontWeight: 700,
                            fontSize: '0.65rem',
                            height: 22,
                        }}
                    />
                </Box>
                {(tour.lead.email || tour.lead.phone) && (
                    <Box className={classes.contactRow}>
                        {tour.lead.email && (
                            <span className={classes.contactItem}>
                                <EmailIcon style={{ fontSize: 13 }} />
                                {tour.lead.email}
                            </span>
                        )}
                        {tour.lead.phone && (
                            <span className={classes.contactItem}>
                                <PhoneIcon style={{ fontSize: 13 }} />
                                {tour.lead.phone}
                            </span>
                        )}
                    </Box>
                )}
                {tour.notes && (
                    <Typography className={classes.notes}>
                        {tour.notes}
                    </Typography>
                )}
                <Box className={classes.tourActions}>
                    {tour.lead.guest_card_completed ? (
                        <Tooltip title="View submitted guest card">
                            <Button
                                size="small"
                                variant="outlined"
                                className={classes.actionBtn}
                                startIcon={
                                    <GuestCardIcon style={{ fontSize: 14 }} />
                                }
                                onClick={() => setViewGuestCardTour(tour)}
                                disabled={isUpdating}
                                style={{
                                    borderColor: '#7c3aed',
                                    color: '#7c3aed',
                                }}
                            >
                                View Guest Card
                            </Button>
                        </Tooltip>
                    ) : (
                        <Tooltip title="Fill out a guest card for this lead">
                            <Button
                                size="small"
                                variant="outlined"
                                className={classes.actionBtn}
                                startIcon={
                                    <GuestCardIcon style={{ fontSize: 14 }} />
                                }
                                onClick={() => setGuestCardTour(tour)}
                                disabled={isUpdating}
                                style={{
                                    borderColor: '#7c3aed',
                                    color: '#7c3aed',
                                }}
                            >
                                Guest Card
                            </Button>
                        </Tooltip>
                    )}
                    {isScheduled && (
                        <>
                            <Tooltip title="Mark tour as completed">
                                <Button
                                    size="small"
                                    variant="outlined"
                                    className={classes.actionBtn}
                                    startIcon={
                                        isUpdating ? (
                                            <CircularProgress size={12} />
                                        ) : (
                                            <CompleteIcon
                                                style={{ fontSize: 14 }}
                                            />
                                        )
                                    }
                                    onClick={() =>
                                        handleMarkStatus(tour, 'completed')
                                    }
                                    disabled={isUpdating}
                                    style={{
                                        borderColor: '#047857',
                                        color: '#047857',
                                    }}
                                >
                                    Complete
                                </Button>
                            </Tooltip>
                            <Tooltip title="Mark as no-show">
                                <Button
                                    size="small"
                                    variant="outlined"
                                    className={classes.actionBtn}
                                    startIcon={
                                        isUpdating ? (
                                            <CircularProgress size={12} />
                                        ) : (
                                            <NoShowIcon
                                                style={{ fontSize: 14 }}
                                            />
                                        )
                                    }
                                    onClick={() =>
                                        handleMarkStatus(tour, 'no_show')
                                    }
                                    disabled={isUpdating}
                                    style={{
                                        borderColor: '#be123c',
                                        color: '#be123c',
                                    }}
                                >
                                    No Show
                                </Button>
                            </Tooltip>
                        </>
                    )}
                </Box>
            </Box>
        )
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={false}
            PaperProps={{ className: classes.paper }}
        >
            <Box className={classes.header}>
                <IconButton
                    className={classes.closeBtn}
                    size="small"
                    onClick={onClose}
                >
                    <Close fontSize="small" />
                </IconButton>
                <Typography className={classes.title}>
                    <span className={classes.titleIcon}>
                        <DirectionsWalk style={{ fontSize: 20 }} />
                    </span>
                    Tours
                </Typography>
                <Typography className={classes.subtitle}>
                    {dateLabel}
                    {localTours.length > 0 &&
                        ` · ${localTours.length} tour${
                            localTours.length !== 1 ? 's' : ''
                        }`}
                </Typography>
            </Box>

            <DialogContent className={classes.content}>
                {localTours.length === 0 ? (
                    <Box className={classes.empty}>
                        <DirectionsWalk
                            style={{
                                fontSize: 40,
                                opacity: 0.3,
                                marginBottom: 8,
                            }}
                        />
                        <Typography variant="body2" style={{ fontWeight: 500 }}>
                            No tours for this day
                        </Typography>
                    </Box>
                ) : (
                    <>
                        {scheduled.length > 0 && (
                            <>
                                <Typography className={classes.sectionLabel}>
                                    Scheduled
                                </Typography>
                                {scheduled.map(renderTour)}
                            </>
                        )}
                        {unscheduled.length > 0 && (
                            <>
                                <Typography className={classes.sectionLabel}>
                                    Unscheduled / Skipped
                                </Typography>
                                {unscheduled.map(renderTour)}
                            </>
                        )}
                    </>
                )}
            </DialogContent>

            <Divider />
            <Box className={classes.footer}>
                <Button
                    onClick={onClose}
                    variant="outlined"
                    style={{
                        textTransform: 'none',
                        borderRadius: 8,
                        fontWeight: 600,
                    }}
                >
                    Close
                </Button>
            </Box>

            <SubmitLeadGuestCardDialog
                open={guestCardTour !== null}
                onClose={() => setGuestCardTour(null)}
                lead={guestCardTour?.lead ?? null}
                onSuccess={async (results, contact) => {
                    if (!guestCardTour) return
                    const leadId = guestCardTour.lead.id
                    setLocalTours((prev) =>
                        prev.map((t) =>
                            t.lead.id === leadId
                                ? {
                                      ...t,
                                      lead: {
                                          ...t.lead,
                                          name: contact.name ?? t.lead.name,
                                          email: contact.email ?? t.lead.email,
                                          phone: contact.phone ?? t.lead.phone,
                                          guest_card_completed: true,
                                          guest_card_results: results,
                                      },
                                  }
                                : t,
                        ),
                    )
                    if (guestCardTour.status === 'scheduled') {
                        await handleMarkStatus(guestCardTour, 'completed')
                    }
                }}
            />

            <ViewGuestCardResultsDialog
                open={viewGuestCardTour !== null}
                onClose={() => setViewGuestCardTour(null)}
                lead={viewGuestCardTour?.lead ?? null}
            />
        </Dialog>
    )
}
